class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.toastTimeout = null;
    this.toastDuration = 3500; // 3.5s
    this._toastEl = null;
    this._injectedStyles = false;

    if (this.notification) {
      this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    }

    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    if (!this.notification) return;
    this.notification.classList.add('animate', 'active');
    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );
    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    if (!this.notification) return;
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();

    let productImage = null;
    try {
      const productHtml = parsedState.sections && parsedState.sections['cart-notification-product'];
      if (productHtml) {
        const doc = new DOMParser().parseFromString(productHtml, 'text/html');
        const img = doc.querySelector('img');
        if (img && img.src) {
          productImage = img.src;
        } else {
          const lazyImg = doc.querySelector('[data-src], [data-lazy-src]');
          if (lazyImg) productImage = lazyImg.getAttribute('data-src') || lazyImg.getAttribute('data-lazy-src');
        }
      }
    } catch (e) {
      console.warn('CartNotification: could not parse product image', e);
    }

    this.showToast({
      text: 'Item Added',
      href: '/cart',
      duration: this.toastDuration,
      productImage: productImage
    });
  }

  showToast({ text = 'Item Added', href = '/cart', duration = 3500, productImage = null } = {}) {
    if (!this._injectedStyles) {
      this.injectToastStyles();
      this._injectedStyles = true;
    }

    if (this._toastEl) {
      clearTimeout(this.toastTimeout);
      this._toastEl.remove();
      this._toastEl = null;
    }

    const toast = document.createElement('a');
    toast.setAttribute('role', 'link');
    toast.setAttribute('href', href);
    toast.setAttribute('tabindex', '0');
    toast.className = 'cn-toast';

    const imgHtml = productImage
      ? `<img class="cn-toast__img" src="${this.escapeHtml(productImage)}" alt="" loading="lazy" />`
      : `<span class="cn-toast__img-placeholder" aria-hidden="true"></span>`;

    // Right-facing arrow SVG
    const arrowSvg = `<svg class="cn-toast__arrow" width="18" height="18" viewBox="0 0 24 24" fill="none"
        xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M9 18l6-6-6-6" stroke="#00412D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;

    toast.innerHTML = `
      <span class="cn-toast__img-wrap">${imgHtml}</span>
      <span class="cn-toast__text">${this.escapeHtml(text)}</span>
      <span class="cn-toast__spacer" aria-hidden="true"></span>
      <span class="cn-toast__actions">
        ${arrowSvg}
      </span>`;

    toast.addEventListener('click', () => {
      try { this.close(); } catch (err) {}
    });

    toast.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toast.click();
      } else if (e.key === 'Escape') {
        this.hideToast();
      }
    });

    document.body.appendChild(toast);
    void toast.offsetWidth;
    toast.classList.add('cn-toast--visible');
    this._toastEl = toast;

    this.toastTimeout = setTimeout(() => {
      this.hideToast();
    }, duration);
  }

  hideToast() {
    if (!this._toastEl) return;
    this._toastEl.classList.remove('cn-toast--visible');
    this._toastEl.classList.add('cn-toast--hidden');
    setTimeout(() => {
      if (this._toastEl) {
        this._toastEl.remove();
        this._toastEl = null;
      }
    }, 420);
    if (this.toastTimeout) {
      clearTimeout(this.toastTimeout);
      this.toastTimeout = null;
    }
  }

  injectToastStyles() {
    const css = `
      .cn-toast {
        position: fixed;
        left: 50%;
        bottom: 28px;
        transform: translateX(-50%) translateY(8px);
        display: inline-flex;
        align-items: center;
        gap: 12px;
        width: min(420px, calc(100% - 40px));
        max-width: 98%;
        padding: 10px 14px;
        background: white;
        color: #00412D;
        box-shadow: 0 10px 34px rgba(0,0,0,0.10);
        border-radius: 10px;
        text-decoration: none;
        font-weight: 700;
        font-size: 15px;
        line-height: 1;
        z-index: 99999;
        opacity: 0;
        transition: transform .32s cubic-bezier(.22,1,.36,1), opacity .32s ease;
        border: 1px solid rgba(0,65,45,0.06);
      }
      .cn-toast--visible { transform: translateX(-50%) translateY(0); opacity: 1; }
      .cn-toast--hidden { transform: translateX(-50%) translateY(8px); opacity: 0; transition: transform .38s ease, opacity .38s ease; }

      .cn-toast__img-wrap {
        flex: 0 0 auto;
        width: 48px; height: 48px;
        display: flex; align-items: center; justify-content: center;
        border-radius: 8px;
        background: rgba(0,0,0,0.02);
        overflow: hidden;
        border: 2px solid #00412D;
      }
      .cn-toast__img { width: 44px; height: 44px; object-fit: cover; display: block; }
      .cn-toast__img-placeholder { width: 44px; height: 44px; background: rgba(0,65,45,0.06); border-radius: 6px; }

      .cn-toast__text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: calc(100% - 140px); }
      .cn-toast__spacer { flex: 1 1 auto; }
      .cn-toast__actions { flex: 0 0 auto; display: flex; align-items: center; }
      .cn-toast__arrow { width: 20px; height: 20px; }

      @media (max-width: 420px) {
        .cn-toast { bottom: 18px; width: calc(100% - 24px); font-size: 14px; padding: 10px 12px; }
        .cn-toast__img-wrap { width: 44px; height: 44px; border-width: 1.8px; }
        .cn-toast__img { width: 40px; height: 40px; }
      }
    `;
    const style = document.createElement('style');
    style.setAttribute('data-cn-toast-styles', '1');
    style.appendChild(document.createTextNode(css));
    document.head.appendChild(style);
  }

  escapeHtml(unsafe) {
    return String(unsafe || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  getSectionsToRender() {
    return [
      { id: 'cart-notification-product', selector: `[id="cart-notification-product-${this.cartItemKey}"]` },
      { id: 'cart-notification-button' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}
customElements.define('cart-notification', CartNotification);
